import { getErrorMessage } from "@entrilia/shared/api/axiosHelper";
import HiddenFileInput from "@entrilia/shared/components/inputs/HiddenFileInput";
import TooltipForDisabledAction from "@entrilia/shared/components/TooltipForDisabledAction";
import { generateGuid } from "@entrilia/shared/utilities/generateGuid";
import { makeTintedBackgroundFromColor } from "@entrilia/shared/utilities/colorHelper";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import { Box, Button, IconButton, InputBase, Paper, Stack, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import type { AssistantAttachment } from "../../../api/types/assistantTypes";
import { attachmentExtensions, messagePayloadError, validateAttachment } from "./attachmentLimits";
import ChatFileChip from "./chat/ChatFileChip";
import type { PendingAttachment } from "./messageDraft";
import { canSendMessage, getReadyAttachments } from "./messageDraft";

interface Props {
  disabled: boolean;
  readOnly?: boolean;
  onUploadFile: (file: File, signal: AbortSignal) => Promise<AssistantAttachment>;
  onSubmit: (prompt: string, attachments: AssistantAttachment[]) => Promise<boolean>;
}

const InputPrompt = ({ disabled, readOnly, onUploadFile, onSubmit }: Props) => {
  const [prompt, setPrompt] = useState("");
  const [attachments, setAttachments] = useState<PendingAttachment[]>([]);

  const uploadsRef = useRef(new Map<string, AbortController>());
  const fileInputRef = useRef<HTMLInputElement>(null);
  const draftRef = useRef({ prompt, attachments });

  useEffect(() => {
    draftRef.current = { prompt, attachments };
  });

  useEffect(() => {
    const uploads = uploadsRef.current;
    return () => uploads.forEach((upload) => upload.abort());
  }, []);

  const readyAttachments = getReadyAttachments(attachments);
  const payloadError = messagePayloadError(readyAttachments);
  const isSubmitDisabled = disabled || !canSendMessage(prompt, attachments);

  const updateAttachment = (key: string, update: Partial<PendingAttachment>) =>
    setAttachments((prev) => prev.map((pending) => (pending.key === key ? { ...pending, ...update } : pending)));

  const startUpload = (key: string, file: File) => {
    const controller = new AbortController();
    uploadsRef.current.set(key, controller);
    onUploadFile(file, controller.signal)
      .then((attachment) => updateAttachment(key, { attachment }))
      .catch((error) => {
        if (!controller.signal.aborted) {
          updateAttachment(key, { error: getErrorMessage(error) });
        }
      })
      .finally(() => uploadsRef.current.delete(key));
  };

  const addFiles = (files: File[]) => {
    let attachedCount = attachments.filter((pending) => !pending.error).length;
    const added = files.map((file) => {
      const key = generateGuid();
      const error = validateAttachment(file, attachedCount);
      if (!error) {
        attachedCount++;
        startUpload(key, file);
      }
      return { key, file, error };
    });
    setAttachments((prev) => [...prev, ...added]);
  };

  const removeAttachment = (key: string) => {
    uploadsRef.current.get(key)?.abort();
    setAttachments((prev) => prev.filter((pending) => pending.key !== key));
  };

  const submit = async () => {
    const sentPrompt = prompt;
    const sentAttachments = attachments;
    setPrompt("");
    setAttachments([]);
    const isSent = await onSubmit(sentPrompt.trim(), readyAttachments);
    const draft = draftRef.current;
    if (!isSent && !draft.prompt && draft.attachments.length === 0) {
      setPrompt(sentPrompt);
      setAttachments(sentAttachments);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === "Enter" && !event.shiftKey && !isSubmitDisabled) {
      event.preventDefault();
      submit();
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLElement>) => {
    if (!disabled && event.clipboardData.files.length > 0 && !event.clipboardData.types.includes("text/plain")) {
      event.preventDefault();
      addFiles(Array.from(event.clipboardData.files));
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    if (!event.dataTransfer.types.includes("Files")) {
      return;
    }
    event.preventDefault();
    if (!disabled) {
      addFiles(Array.from(event.dataTransfer.files));
    }
  };

  return (
    <Box sx={{ width: "100%" }} onDrop={handleDrop}>
      {attachments.length > 0 && (
        <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap", mb: 1 }}>
          {attachments.map((pending) => (
            <ChatFileChip
              key={pending.key}
              name={pending.file.name}
              isUploading={!pending.attachment && !pending.error}
              error={pending.error}
              onDelete={() => removeAttachment(pending.key)}
            />
          ))}
        </Stack>
      )}
      {payloadError && (
        <Typography variant="caption" color="error" sx={{ display: "block", mb: 1 }}>
          {payloadError}
        </Typography>
      )}
      <HiddenFileInput ref={fileInputRef} acceptedFileTypes={attachmentExtensions} multiple onFilesAdd={addFiles} />
      <Paper
        variant="outlined"
        sx={(t) => ({
          "&:focus-within": {
            borderColor: "primary.main",
            boxShadow: `0 0 0 1px ${makeTintedBackgroundFromColor(t, t.palette.primary.main)}`,
          },
        })}
      >
        <InputBase
          placeholder="Type your request..."
          disabled={readOnly}
          maxRows={4}
          multiline
          fullWidth
          data-gramm="false"
          spellCheck={false}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          sx={{ px: 2, pt: 2, pb: 1 }}
        />
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 1, pb: 1 }}>
          <TooltipForDisabledAction title="Attach files" isActionDisabled={disabled}>
            <IconButton disabled={disabled} onClick={() => fileInputRef.current?.click()}>
              <AddRoundedIcon />
            </IconButton>
          </TooltipForDisabledAction>
          <Button variant="contained" disabled={isSubmitDisabled} sx={{ minWidth: 0, p: 1 }} onClick={submit}>
            <ArrowUpwardRoundedIcon sx={{ fontSize: "1.125rem" }} />
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default InputPrompt;
